import React, { useMemo } from "react";
import dynamic from "next/dynamic";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import renderItem from "utils/renderItem";


import { colors, resolveColor } from "utils/styles";

import ImageCard from "./ImageCard";
const DynamicProductCard = dynamic(() => import("./ProductCard"), {
	loading: () => <div />,
});
const DynamicBioCard = dynamic(() => import("./BioCard"), {
	loading: () => <div />,
});
const DynamicArticleCard = dynamic(() => import("./ArticleCard"), {
	loading: () => <div />,
});
const DynamicCenteredContainer = dynamic(() => import("./CenteredContainer"), {
	loading: () => <div />,
});

const DynamicJournalEditionCard = dynamic(() => import("./JournalEditionCard"), {
	loading: () => <div />,
});
const DynamicContent = dynamic(() => import("./Content"), {
	loading: () => <div></div>,
});
const DynamicProductQuote = dynamic(() => import("./ProductQuote"), {
	loading: () => <div></div>,
});

export default function MediaCardSection(props) {

	const fields = props.data?.fields;

	// Memoize spacing calculations to prevent unnecessary re-renders
	const spacingStyles = useMemo(() => ({
		marginTop: fields?.marginTop || 0,
		paddingTop: fields?.paddingTop || 0,
		paddingBottom: fields?.paddingBottom || 0,
		marginBottom: fields?.marginBottom || 0,
		marginTopMobile: (fields?.marginTop || 0) / 2,
		paddingTopMobile: (fields?.paddingTop || 0) / 2,
		paddingBottomMobile: (fields?.paddingBottom || 0) / 2,
		marginBottomMobile: (fields?.marginBottom || 0) / 2
	}), [fields?.marginTop, fields?.paddingTop, fields?.paddingBottom, fields?.marginBottom]);

	const LgColCount = fields?.columnCount ? 12 / fields?.columnCount : fields?.columnCount == 0 ? "auto" : 4;
	const MdColCount =
		fields?.columnCount < 3
			? 12 / fields?.columnCount
			: fields?.columnCount >= 3
			? 12 / Math.ceil(fields.columnCount / 2)
			: 12;

	const xsColumnCount = fields?.extraSmallColumnCount ? 12 / fields?.extraSmallColumnCount : 12;
	const SmColCount = fields?.smallColumnCount
		? 12 / fields?.smallColumnCount
		: xsColumnCount
		? xsColumnCount
		: LgColCount > 4
		? 12
		: 6;

	const isContainer = fields?.isContainer ? fields?.isContainer : false;
	const isWideContainer = fields?.isWideContainer ? fields?.isWideContainer : false;

	const hashLink = fields?.hashLink;

	const marginTop = fields?.marginTop !== undefined ? fields?.marginTop : 0;
	const marginBottom = fields?.marginBottom !== undefined ? fields?.marginBottom : 0;
	const paddingTop = fields?.paddingTop !== undefined ? fields?.paddingTop : 2;
	const paddingBottom = fields?.paddingBottom !== undefined ? fields?.paddingBottom : 2;
	const cardClasses = fields?.cardClasses ? fields?.cardClasses : " ";
	const containerClasses = fields?.containerClasses || " ";
	const isCarousel = fields?.isCarousel;

	function Carousel(props = {}) {
		const {
			items,
			mediumItems = 2,
			smallItems = 1,
			itemsLarge = 3,
			partialVisibilityGutter = 40,
			itemClass = "",
		} = props;
		const HorizontalScrollProductCollection = dynamic(() => import("./HorizontalScrollProductCollection"), {
			loading: () => <div></div>,
		});

		return (
			<HorizontalScrollProductCollection
				data={{ horizontalScrollData: { items } }}
				itemsLarge={itemsLarge}
				mediumItems={mediumItems}
				smallItems={smallItems}
				partialVisibilityGutter={partialVisibilityGutter}
				itemClass={itemClass}
			/>
		);
	}

	return (
		<section id={hashLink} className={`outerWrapper  MediaCardSection ${containerClasses}`}>
			{/* <Container fluid className={`g-0`}> */}
			<div className="innerWrapper">
				<Container
					fluid
					className={`${
						isContainer
							? `container-${isContainer}`
							: isWideContainer
							? `wide-container-${isWideContainer}`
							: " "
					} g-0 `}
				>
					{fields.contents?.length > 0 && (
						<Row className={`justify-content-center px-4 pb-0 text-center g-0`}>
							{fields?.contents.map((component, i) => {
								const model = component?.sys?.contentType?.sys.id;

								const key = component?.sys?.id;

								if (model === "metaTitle") {
									return <React.Fragment key={key}>{renderItem(component)}</React.Fragment>;
								} else if (model === "title") {
									return <React.Fragment key={key}>{renderItem(component)}</React.Fragment>;
								} else if (model === "buttonGroup") {
									return <React.Fragment key={key}>{renderItem(component)}</React.Fragment>;
								} else if (model === "content") {
									return (
										<Col key={key} xs={12} md={9} lg={7}>
											{renderItem(component)}
										</Col>
									);
								} else {
									return <React.Fragment key={i}></React.Fragment>;
								}
							})}{" "}
						</Row>
					)}

					<div className={`row g-0 gy-3 justify-content-center d-flex px-2 `}>
						{fields?.mediaCards?.length > 0 &&
							(isCarousel ? (
								<Carousel
									items={fields?.mediaCards}
									itemsLarge={1}
									mediumItems={1}
									smallItems={1}
									partialVisibilityGutter={0}
									// itemClass="w-100"
								/>
							) : (
								fields?.mediaCards.map((card, index) => {
									//could be a media or product content type
									let contentType = card?.sys?.contentType?.sys?.id;
									//graphQL cannot query this field
									if (!contentType) {
										if (card.productType === "Book" || card.productType === "Course") {
											contentType = "product";
										} else {
											console.error("WHAT IS THIS????");
										}
									}

									return (
										<Col
											key={card.sys.id}
											className={`justify-content-center align-items-stretch  d-flex px-2 ${cardClasses}`}
											xs={xsColumnCount}
											sm={SmColCount}
											md={MdColCount}
											lg={LgColCount}
										>
											{contentType === "content" && <DynamicContent content={card} />}
											{contentType === "media" && <ImageCard data={card} />}
											{(contentType === "product" || contentType === "variant") && (
												<DynamicProductCard entryId={card.sys.id} data={card} />
											)}
											{contentType === "basicArticle" && (
												<DynamicArticleCard entryId={card.sys.id} data={card} />
											)}
											{contentType === "centeredContainer" && (
												<DynamicCenteredContainer data={card} />
											)}
											{contentType === "bio" && (
												<DynamicBioCard entryId={card.sys.id} data={card} />
											)}
											{contentType === "journalEditions" && (
												<DynamicJournalEditionCard data={card} />
											)}
											{contentType === "productQuote" && <DynamicProductQuote data={card} />}
										</Col>
									);
								})
							))}
					</div>

					{fields?.bottomComponents && (
						<Row className="g-0 justify-content-center">
							{fields?.bottomComponents.map((component) => {
								const align = component?.fields?.textAlign
									? `justify-content-${component?.fields?.textAlign}`
									: "justify-content-center";
								return (
									<Col className={`${align} d-flex w-100 `} key={component.sys.id}>
										{renderItem(component)}
									</Col>
								);
							})}
						</Row>
					)}
				</Container>
			</div>
			{/* </Container> */}

			<style jsx="true">{`
				.outerWrapper {
					background: ${resolveColor(fields?.backgroundColor) || colors.lightGray};
					margin-top: ${spacingStyles.marginTop}em;
					padding-top: ${spacingStyles.paddingTop}em;
					padding-bottom: ${spacingStyles.paddingBottom}em;
					margin-bottom: ${spacingStyles.marginBottom}em;
				}

				@media (max-width: 767.98px) {
					.outerWrapper {
						margin-top: ${spacingStyles.marginTopMobile}em;
						padding-top: ${spacingStyles.paddingTopMobile}em;
						padding-bottom: ${spacingStyles.paddingBottomMobile}em;
						margin-bottom: ${spacingStyles.marginBottomMobile}em;
					}
				}

				.innerWrapper {
					background: ${resolveColor(fields?.backgroundColor) || colors.lightGray};
				}
			`}</style>
		</section>
	);
}
